<script>
import { formButton, formInput, formMoney, formSelect, greatherThan } from 'tb-design_system-web'
import { useForm } from 'vee-validate'
import { onMounted, ref, provide, watch } from 'vue'
import { useMeta } from 'vue-meta'
import { useRouter } from 'vue-router'
import * as yup from 'yup'

import formBankAccount from '@/components/shared/formBankAccount.vue'
import RegisterLayout from '@/components/shared/registerLayout.vue'
import StepProgressBar from '@/components/shared/stepProgressBar.vue'
import { moneyFormat } from '@/composables/utils/moneyConfig'
import { validateStepAccess } from '@/composables/utils/onboardingGuards'
import { handleGlobalError } from '@/services/errorHandler'
import { useCache } from '@/stores/cache'
import { useOnboardingStore } from '@/stores/onboarding'

export default {
  name: 'RegisterFinishPFStep2',
  components: {
    RegisterLayout,
    StepProgressBar,
    formBankAccount,
    formInput,
    formMoney,
    formSelect,
    formButton
  },
  setup() {
    useMeta({ title: 'Cadastro PF - Etapa 2/3' })

    const router = useRouter()
    const onboardingStore = useOnboardingStore()
    const cacheStore = useCache()

    const step2Data = onboardingStore.getStepData(2) || {}

    const loading = ref(false)
    const banksValue = ref(
      step2Data?.banks?.length ? [...step2Data.banks] : [{ bankId: '', agency: '', account: '' }]
    )

    const isAnnualMoreThan50Value = ref(step2Data?.isAnnualMoreThan50 ?? null)
    const annualTargetVolumeValue = ref(step2Data?.annualTargetVolume ?? '')
    const foreignCounterpartyCountryValue = ref(step2Data?.foreignCounterpartyCountry ?? '')
    const foreignCounterpartyNameValue = ref(step2Data?.foreignCounterpartyName ?? '')
    const countryOptions = [...cacheStore.countries]

    onMounted(async () => {
      validateStepAccess(onboardingStore, 'PF', [1], router)
    })

    const onSubmitAction = () => {
      alreadySubmittedOnce.value = true
      return onSubmit()
    }

    const alreadySubmittedOnce = ref(false)
    provide('alreadySubmittedOnce', alreadySubmittedOnce)

    const { handleSubmit, errors, setFieldValue } = useForm({
      validationSchema: yup.object({
        banks: yup.array().of(
          yup.object().shape({
            id: yup.object().required(),
            agency: yup.string().required(),
            account: yup.string().required(),
            ownerName: yup.string().required()
          })
        ),
        isAnnualMoreThan50: yup.boolean().nullable().required('Selecione uma opção'),
        annualTargetVolume: yup.string().when('isAnnualMoreThan50', {
          is: true,
          then: () => greatherThan(yup),
          otherwise: () => yup.string().notRequired()
        }),
        foreignCounterpartyCountry: yup.string().when('isAnnualMoreThan50', {
          is: true,
          then: () => yup.string().required('Campo obrigatório'),
          otherwise: () => yup.string().notRequired()
        }),
        foreignCounterpartyName: yup.string().when('isAnnualMoreThan50', {
          is: true,
          then: () => yup.string().required('Campo obrigatório'),
          otherwise: () => yup.string().notRequired()
        })
      }),
      initialValues: {
        isAnnualMoreThan50: isAnnualMoreThan50Value.value
      }
    })

    watch(isAnnualMoreThan50Value, (val) => {
      setFieldValue('isAnnualMoreThan50', val)
    })

    const onSubmit = handleSubmit(async () => {
      loading.value = true
      try {
        if (annualTargetVolumeValue.value === '') annualTargetVolumeValue.value = '0.00'
        onboardingStore.setStepData(2, {
          banks: banksValue.value,
          isAnnualMoreThan50: isAnnualMoreThan50Value.value,
          annualTargetVolume: annualTargetVolumeValue.value,
          foreignCounterpartyCountry: foreignCounterpartyCountryValue.value,
          foreignCounterpartyName: foreignCounterpartyNameValue.value
        })
        router.push('/finalizar-cadastro/documentos')
      } catch (err) {
        handleGlobalError(err)
      } finally {
        loading.value = false
      }
    })

    return {
      loading,
      onSubmitAction,

      moneyFormat,
      isAnnualMoreThan50Value,
      annualTargetVolumeValue,
      foreignCounterpartyCountryValue,
      foreignCounterpartyNameValue,
      countryOptions,
      banksValue,

      errors,
      alreadySubmittedOnce
    }
  }
}
</script>

<template>
  <RegisterLayout>
    <div class="col-12 col-md-9 h-100 onboarding pb-5">
      <div class="row">
        <StepProgressBar
          class="mt-5 mb-5"
          :steps="['Dados Pessoais', 'Financeiro', 'Anexos']"
          :current="2"
        />
      </div>
      <div class="row d-flex justify-content-center mt-4">
        <div class="col-12 col-md-6 col-lg-5 d-flex flex-column align-items-center">
          <form-bank-account v-model="banksValue" :allowMultiple="false" />

          <div class="col-12 mt-3">
            <p class="text-center">Volume pretendido de câmbio anual</p>
            <div class="radio-card-group">
              <label
                class="radio-card-label"
                :class="{ selected: isAnnualMoreThan50Value === false }"
              >
                <input
                  type="radio"
                  :value="false"
                  v-model="isAnnualMoreThan50Value"
                  name="volume"
                />
                <span class="custom-radio"></span>
                <span class="label-text">Até USD 50.000,00</span>
              </label>
              <label
                class="radio-card-label"
                :class="{ selected: isAnnualMoreThan50Value === true }"
              >
                <input type="radio" :value="true" v-model="isAnnualMoreThan50Value" name="volume" />
                <span class="custom-radio"></span>
                <span class="label-text">Acima de USD 50.000,00</span>
              </label>
            </div>
          </div>
          <div
            v-if="alreadySubmittedOnce && errors.isAnnualMoreThan50"
            class="text-danger text-center error"
          >
            {{ errors.isAnnualMoreThan50 }}
          </div>

          <div v-if="isAnnualMoreThan50Value" class="col-12">
            <form-money
              className="col-12 form-input"
              v-model="annualTargetVolumeValue"
              label="Informe o valor aproximado"
              id="annualTargetVolume"
              name="annualTargetVolume"
              :config="moneyFormat('USD ', '.', ',', 2)"
            />

            <form-input
              class="col-12 form-input mb-3"
              label="Informe o nome da pessoa ou da empresa no exterior que vai receber ou enviar valores de ou para você"
              v-model="foreignCounterpartyNameValue"
              id="foreignCounterpartyName"
              name="foreignCounterpartyName"
            />

            <form-select
              class="col-12 form-input mb-3"
              v-model="foreignCounterpartyCountryValue"
              label="Informe o país dessa pessoa ou empresa"
              :items="countryOptions"
              id="foreignCounterpartyCountry"
              name="foreignCounterpartyCountry"
            />
          </div>

          <form-button
            class="col-12 form-input"
            title="Próximo"
            :loading="loading"
            :isDisabled="loading"
            @clicked="onSubmitAction"
          />
        </div>
      </div>
    </div>
  </RegisterLayout>
</template>

<style lang="scss" scoped></style>
